import React, { useState, useRef } from "react";
import { Steps, Button, message, Spin } from "antd";
import { useParams, useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Step1ConfigureExperiment from "./Step1ConfigureExperiment";
import Step2CalculatePower from "./Step2CalculatePower";
import Step3ReviewAndExport from "./Step3ReviewAndExport";
import { GeoLiftJobConfigService } from "../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import GeoLiftApiService from "../../../services/api/geoLiftApi";
import axiosInstance from "../../../utils/axiosInstance";

const getTenantId = () => {
  return localStorage.getItem("tenantId") || "default-tenant";
};

const PowerCalculatorStudy: React.FC = () => {
  const location = useLocation();
  const urlId = useParams<{ id: string }>();
  const id = location?.state?.calculationId ?? urlId.id;

  const [current, setCurrent] = useState(0);
  const step1Ref = useRef<any>(null);
  const [calculating, setCalculating] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(false);

  const [experimentData, setExperimentData] = useState<any>({
    experimentName: "",
    statisticalPower: 0.8,
    targetCPIC: 0,
    campaignBudget: 0,
    bauBudget: 0,
    fileList: [],
    includeMarket: "",
    treatmentDuration: 9,
    effectSizes: "10,25,50",
  });

  const [powerResults, setPowerResults] = useState<any>(null);
  const [jobConfigId, setJobConfigId] = useState<string>(id || uuidv4());

  const handleExperimentDataChange = (values: any) => {
    setExperimentData(values);
  };

  /**
   * Call backend to calculate power analysis
   * This follows the same pattern as the existing GeoLift measurement flow
   */
  const calculatePowerResults = async (data: any) => {
    setCalculating(true);

    try {
      const geoLiftService = new GeoLiftJobConfigService();
      const tenantId = getTenantId();
      const newJobConfigId = jobConfigId || uuidv4();

      // Step 1: Upload CSV file to S3
      message.loading({ content: "Uploading data file...", key: "upload", duration: 0 });

      const csvFile = data.fileList[0];
      const s3Path = `${tenantId}/${newJobConfigId}/input/geo_lift_data.csv`;

      await geoLiftService.uploadFile(csvFile, s3Path);
      message.success({ content: "File uploaded successfully", key: "upload", duration: 2 });

      // Step 2: Create job config with parameters
      message.loading({ content: "Creating job configuration...", key: "jobconfig", duration: 0 });

      const effectSizesArray = data.effectSizes
        .split(',')
        .map((s: string) => parseFloat(s.trim()) / 100); // Convert to decimals

      const jobConfigData: any = {
        jobConfigId: newJobConfigId,
        tenantId: tenantId,
        name: data.experimentName,
        status: "DRAFT",
        modelParameterConfig: {
          parameters: {
            isNationalBuy: false,
            includeMarket: data.includeMarket,
            treatmentPeriods: [data.treatmentDuration],
            effectSizes: effectSizesArray,
            cpic: data.targetCPIC,
            power: data.statisticalPower,
            budget: data.campaignBudget,
            lookbackWindow: 1,
            ns: 1000, // Number of simulations
          },
          confidence: 0.8,
        },
      };

      await geoLiftService.createJobConfig(newJobConfigId, jobConfigData as any);
      setJobConfigId(newJobConfigId);
      message.success({ content: "Job configuration created", key: "jobconfig", duration: 2 });

      // Step 3: Trigger the GeoLift power analysis job via SQS
      message.loading({ content: "Running power analysis (this may take 2-5 minutes)...", key: "analysis", duration: 0 });

      // The backend SQS listener will process this job config and generate BestMarkets
      // We trigger it by calling the job config API with a status update
      await axiosInstance.put(`/api/jobConfig/${newJobConfigId}`, {
        ...jobConfigData,
        status: "PENDING",
      });

      // Wait a bit for the job to start processing
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Step 4: Poll for results (BestMarkets data)
      // In the real flow, BestMarkets are stored in S3 after the job completes
      // For now, we'll call the market selection API which should have the results

      let attempts = 0;
      const maxAttempts = 60; // 5 minutes max (60 * 5 seconds)
      let results = null;

      while (attempts < maxAttempts) {
        try {
          // Try to get the results - the first market (ID=0) from BestMarkets
          const marketSelectionResult = await GeoLiftApiService.calculatePower(
            newJobConfigId,
            tenantId,
            0, // First market selection ID
            data.includeMarket
          );

          if (marketSelectionResult && marketSelectionResult.marketSelection) {
            results = marketSelectionResult;
            break;
          }
        } catch (error: any) {
          // If 404 or no data yet, keep polling
          if (attempts < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
            attempts++;
          } else {
            throw error;
          }
        }
      }

      if (!results) {
        throw new Error("Power analysis timed out. Please try again.");
      }

      message.success({ content: "Power analysis completed!", key: "analysis", duration: 3 });

      // Parse and format results for display
      const formattedResults = formatPowerResults(results, data);
      setPowerResults(formattedResults);

      return formattedResults;

    } catch (error: any) {
      console.error("Power calculation error:", error);
      message.error({
        content: error.message || "Failed to calculate power analysis. Please try again.",
        key: "analysis",
        duration: 5,
      });
      throw error;
    } finally {
      setCalculating(false);
    }
  };

  /**
   * Format backend results for display in Step 2
   */
  const formatPowerResults = (backendResults: any, inputData: any) => {
    const marketData = backendResults.marketSelection;

    // Extract BestMarkets data
    // BestMarkets format: [column_names, [row1_data, row2_data, ...]]
    const bestMarketsColumns = marketData.BestMarkets?.[0] || [];
    const bestMarketsData = marketData.BestMarkets?.[1] || [];

    // Get the first (best) market result
    const bestMarket = bestMarketsData[0] || {};

    // Map column names to values
    const resultMap: any = {};
    bestMarketsColumns.forEach((col: string, idx: number) => {
      resultMap[col] = bestMarket[idx];
    });

    return {
      // From backend
      location: resultMap.location || inputData.includeMarket,
      duration: resultMap.duration || inputData.treatmentDuration,
      investment: resultMap.Investment || 0,
      avgATT: resultMap.AvgATT || 0,
      predictedLiftPercent: resultMap.abs_lift_in_perc || 0,
      power: resultMap.Power || 0,
      effectSize: resultMap.EffectSize || 0,
      avgScaledL2Imbalance: resultMap.AvgScaledL2Imbalance || 0,

      // From input
      targetCPIC: inputData.targetCPIC,
      campaignBudget: inputData.campaignBudget,
      bauBudget: inputData.bauBudget,

      // Calculated
      incrementalBudget: inputData.campaignBudget - inputData.bauBudget,
      testMarkets: resultMap.test_markets || [inputData.includeMarket],

      // Full backend data for detailed view
      allMarkets: bestMarketsData,
      marketColumns: bestMarketsColumns,
      rawData: backendResults,
    };
  };

  const isNextDisabled = () => {
    switch (current) {
      case 0:
        return (
          !experimentData.experimentName ||
          !experimentData.targetCPIC ||
          !experimentData.campaignBudget ||
          !experimentData.bauBudget ||
          !experimentData.includeMarket ||
          !experimentData.treatmentDuration ||
          !experimentData.fileList ||
          experimentData.fileList.length === 0 ||
          calculating
        );
      case 1:
        return !powerResults || calculating;
      case 2:
        return false;
      default:
        return false;
    }
  };

  const handleNext = async () => {
    if (current === 0) {
      // Calculate power when moving from step 1 to step 2
      try {
        await calculatePowerResults(experimentData);

        // Only move to next step if calculation succeeded
        if (current < steps.length - 1) {
          setCurrent(current + 1);
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        // Error already handled in calculatePowerResults
        return;
      }
    } else {
      if (current < steps.length - 1) {
        setCurrent(current + 1);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    }
  };

  const handlePrev = () => {
    setCurrent((current) => Math.max(current - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const steps = [
    {
      title: "Configure Experiment",
      content: (
        <Step1ConfigureExperiment
          ref={step1Ref}
          onValuesChange={handleExperimentDataChange}
          initialValues={experimentData}
          setFileUploaded={setFileUploaded}
        />
      ),
    },
    {
      title: "Calculate Power",
      content: calculating ? (
        <div className="flex flex-col items-center justify-center h-96">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Running power analysis...</p>
          <p className="text-sm text-gray-400">This may take 2-5 minutes</p>
        </div>
      ) : (
        <Step2CalculatePower
          experimentData={experimentData}
          powerResults={powerResults}
        />
      ),
    },
    {
      title: "Review & Export",
      content: (
        <Step3ReviewAndExport
          experimentData={experimentData}
          powerResults={powerResults}
        />
      ),
    },
  ];

  return (
    <div className="w-full mx-auto bg-white rounded-lg shadow-sm">
      <div className="flex h-[calc(100vh-75px)]">
        {/* Stepper Section */}
        <div
          className="w-1/4 pr-4 ml-6 overflow-y-auto"
          style={{ height: "calc(100vh - 120px)" }}
        >
          <h2 className="text-2xl font-semibold mt-5 mb-6 text-gray-900">
            {experimentData.experimentName || "Power Calculator"}
          </h2>

          <Steps
            direction="vertical"
            current={current}
            onChange={(value) => {
              // Allow navigation to previous steps only
              if (value < current && !calculating) {
                setCurrent(value);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            items={steps.map((step) => ({
              title: step.title,
            }))}
            className="[&_.ant-steps-item]:mb-12 [&_.ant-steps-item-tail]:!top-3 [&_.ant-steps-item-tail]:!h-[100px] [&_.ant-steps-item-tail]:!w-[3px] [&_.ant-steps-item-icon]:!text-lg [&_.ant-steps-item-icon]:!w-8 [&_.ant-steps-item-icon]:!h-8 [&_.ant-steps-item-icon]:!font-bold [&_.ant-steps-item-icon]:!flex [&_.ant-steps-item-icon]:!items-center [&_.ant-steps-item-icon]:!justify-center [&_.ant-steps-item-title]:!font-medium [&_.ant-steps-item-title]:!text-base"
          />
        </div>

        {/* Content Section */}
        <div
          className="py-6 w-3/4 px-8 border-l overflow-y-auto"
          style={{ height: "calc(100vh - 120px)" }}
        >
          <div className="pb-16">{steps[current].content}</div>
        </div>
      </div>

      {/* Button Section */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex justify-between z-50">
        <div>
          {current > 0 && (
            <Button
              onClick={handlePrev}
              size="large"
              className="mr-2"
              icon={<span className="mr-1">←</span>}
              disabled={calculating}
            >
              Previous
            </Button>
          )}
        </div>

        <div>
          {current < steps.length - 1 && (
            <Button
              type="primary"
              size="large"
              onClick={handleNext}
              disabled={isNextDisabled()}
              loading={calculating}
              icon={!calculating && <span className="ml-1">→</span>}
            >
              {current === 0 ? "Calculate Power" : "Next"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PowerCalculatorStudy;
